import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Box, ClickAwayListener, InputBase, Popper, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import AddRounded from "@mui/icons-material/AddRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import { ACCESS_CHANGED_EVENT } from "services/utils/companyAccess";
import {
  APP_BAR_HEIGHT,
  PANEL_BACKDROP_COLOR,
  SIDEBAR_PANEL_WIDTH_VAR,
  SIDEBAR_RAIL_WIDTH,
  designColors,
  designFontFamily,
  designRadii,
  designWeights,
} from "services/constants/designTokens";
import {
  EMPLOYEE_PAGE_PATH,
  canOpenEmployeePanel,
  employeePanelRoute,
  findEntryForLocation,
  quickActionsFor,
} from "./searchRegistry";
import { canOpenRoute, searchProducts } from "./searchAccess";
import { routeKey, sidebarPageFor, withLocalPages } from "./localPages";
import { matchesAccessContext } from "services/utils/companyAccess";
import {
  GROUP_LIMIT,
  SEE_ALL_LIMIT,
  addLocalRecent,
  addRecent,
  clearProductAccessCache,
  getLocalRecent,
  getRecent,
  getRegistry,
  loadProductAccess,
  search,
} from "./searchApi";

// Top-bar search (Cauver Global Search spec v1.0) on the search APIs
// (searchApi.js): takes the user to a page, a setting, a quick action or an
// employee. It does not filter tables; each table keeps its own search.
// Results come filtered and ranked from the server and are shown as is.

const DEBOUNCE_MS = 200;

// Dropdown positioning. Kept outside the component on purpose: new objects
// on every render make the Popper rebuild its positioning, which drew the
// panel in the top-left corner for a frame (a visible blink) whenever its
// content changed. Fixed like the top bar, so scrolling doesn't move it.
const POPPER_OPTIONS = { strategy: "fixed" };
const POPPER_MODIFIERS = [
  { name: "offset", options: { offset: [0, 6] } },
  { name: "flip", enabled: false },
  { name: "preventOverflow", options: { altAxis: false, padding: 12 } },
];
const LISTBOX_ID = "global-search-listbox";
const optionId = (index) => `global-search-option-${index}`;
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

// The server needs 2 letters/digits; spaces and punctuation don't count.
const isLongEnough = (text) => text.replace(/[^\p{L}\p{N}]/gu, "").length >= 2;

const GROUPS = [
  { key: "pages", label: "Pages" },
  { key: "settings", label: "Settings" },
  { key: "actions", label: "Quick actions" },
  { key: "employees", label: "Employees" },
];

const TYPE_ICON = {
  page: DescriptionOutlined,
  setting: SettingsOutlined,
  action: AddRounded,
};

const initialsOf = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

// One Recent row per page (or quick action): same type + same route.
const recentKey = (item) => `${item?.type}|${routeKey(item?.route)}`;

const isTypingTarget = (target) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

// Text with the matched letters bold and highlighted (not colour alone).
// Ranges are [start, end) on the text exactly as the API returned it.
const Highlighted = ({ text, ranges }) => {
  if (!ranges?.length) return text;
  const parts = [];
  let cursor = 0;
  [...ranges]
    .sort((a, b) => a[0] - b[0])
    .forEach(([start, end]) => {
      const from = Math.max(start, cursor);
      if (end <= from) return;
      if (from > cursor) parts.push(text.slice(cursor, from));
      parts.push(
        <Box
          key={from}
          component="mark"
          sx={{ backgroundColor: "#FEF08A", color: "inherit", fontWeight: designWeights.heading, borderRadius: "2px", px: "1px" }}
        >
          {text.slice(from, end)}
        </Box>,
      );
      cursor = end;
    });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
};

const Kbd = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      fontFamily: designFontFamily,
      fontSize: 11,
      lineHeight: "16px",
      px: 0.5,
      border: `1px solid ${designColors.slate300}`,
      borderRadius: "4px",
      color: designColors.slate600,
      backgroundColor: designColors.white,
    }}
  >
    {children}
  </Box>
);

export const GlobalSearch = ({ routes, sidebarOpen, onOpen }) => {
  const history = useHistory();
  const location = useLocation();
  const {
    domain,
    topbarCompanyId: companyId,
    currentLocation,
    userDetails,
    companyList,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);

  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searchFailed, setSearchFailed] = useState(false);
  const [seeAll, setSeeAll] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // ---- Registry: what the user may open. Loaded after login, when the
  // company changes and when permissions change. Drives the quick actions
  // and which page opens go to Recent. ----
  const [registry, setRegistry] = useState([]);
  const [registryVersion, setRegistryVersion] = useState(0);
  useEffect(() => {
    const reload = () => setRegistryVersion((n) => n + 1);
    window.addEventListener(ACCESS_CHANGED_EVENT, reload);
    return () => window.removeEventListener(ACCESS_CHANGED_EVENT, reload);
  }, []);
  useEffect(() => {
    if (!domain || !companyId) {
      setRegistry([]);
      return undefined;
    }
    const controller = new AbortController();
    getRegistry({ domain, companyId, signal: controller.signal })
      .then((items) => !controller.signal.aborted && setRegistry(items))
      .catch(() => !controller.signal.aborted && setRegistry([]));
    return () => controller.abort();
  }, [domain, companyId, registryVersion]);

  // ---- Recent (spec 4.2: the last 6 pages/settings opened, newest
  // first). Every page that opens (from search, the sidebar or a direct
  // link) is sent to the server when the registry knows it, and is also
  // kept in this browser (searchApi.js), which covers pages the registry
  // doesn't list yet. Once per page visit. ----
  const lastRecordedRef = useRef("");
  const recentScope = useMemo(
    () => ({ domain, userId: userDetails?.id, companyId }),
    [domain, userDetails?.id, companyId],
  );
  useEffect(() => {
    const entry =
      findEntryForLocation(registry, location.pathname, location.search) ||
      sidebarPageFor(routes, location.pathname, location.search);
    const visit = `${location.pathname}${location.search}|${entry?.id}`;
    if (!entry || lastRecordedRef.current === visit) return;
    lastRecordedRef.current = visit;
    if (!String(entry.id).startsWith("local:")) addRecent({ domain, id: entry.id });
    addLocalRecent(recentScope, recentKey(entry), entry);
  }, [registry, routes, location.pathname, location.search, domain, recentScope]);

  // Loaded after login and refreshed each time the panel opens: this browser's list first (newest
  // first, includes the page just opened), then the server's (other
  // devices), each page once, 6 at most. The list on screen stays while the
  // server answers and is only replaced if it changed, so opening the panel
  // doesn't blink ("Loading…" only before the very first list exists).
  const [recent, setRecent] = useState(null);
  const lastServerRecentRef = useRef({ companyId: null, items: [] });
  useEffect(() => {
    if (!domain || !companyId) return undefined;
    // Loaded once in the background after login (and per company), so the
    // first open already has the full list; refreshed again on every open.
    const loadedForCompany = String(lastServerRecentRef.current.companyId) === String(companyId);
    if (!open && loadedForCompany) return undefined;
    const controller = new AbortController();
    const sameList = (a, b) =>
      Array.isArray(a) && a.length === b.length && a.every((item, index) => recentKey(item) === recentKey(b[index]));
    const show = (next) => setRecent((prev) => (sameList(prev, next) ? prev : next));
    const merge = (serverItems) => {
      const seen = new Set();
      return [...getLocalRecent(recentScope), ...serverItems]
        .filter((item) => {
          const key = recentKey(item);
          if (!item.route || seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, 6);
    };
    // Right away: this browser's list with the server's last answer.
    const known = lastServerRecentRef.current;
    show(merge(String(known.companyId) === String(companyId) ? known.items : []));
    getRecent({ domain, companyId, signal: controller.signal })
      .then((items) => {
        if (controller.signal.aborted) return;
        lastServerRecentRef.current = { companyId, items };
        show(merge(items));
      })
      .catch(() => {});
    return () => controller.abort();
  }, [open, domain, companyId, recentScope]);

  // ---- Second permission check (searchAccess.js): each product's
  // provisioning, loaded when the search opens. ----
  const companies = useMemo(() => {
    if (Array.isArray(companyList)) return companyList;
    try {
      return JSON.parse(companyList || "[]");
    } catch {
      return [];
    }
  }, [companyList]);
  const productNames = searchProducts(routes).map((product) => product.name).join("|");
  const [loadedAccess, setLoadedAccess] = useState({ companyId: null, byProduct: {} });
  const [accessVersion, setAccessVersion] = useState(0);
  useEffect(() => {
    const reload = () => {
      clearProductAccessCache();
      setLoadedAccess({ companyId: null, byProduct: {} });
      setAccessVersion((n) => n + 1);
    };
    window.addEventListener(ACCESS_CHANGED_EVENT, reload);
    return () => window.removeEventListener(ACCESS_CHANGED_EVENT, reload);
  }, []);
  useEffect(() => {
    // Loaded in the background after login (cached 5 minutes), so it is
    // ready before the search opens; opening it retries any that failed.
    if (!domain || !companyId || !userDetails?.id) return undefined;
    let cancelled = false;
    setLoadedAccess((prev) => ({
      ...prev,
      byProduct: Object.fromEntries(Object.entries(prev.byProduct).filter(([, access]) => !access?.failed)),
    }));
    productNames.split("|").filter(Boolean).forEach((productName) => {
      loadProductAccess({
        domain,
        userId: userDetails.id,
        userType: userDetails.type,
        companyId,
        companies,
        productName,
      }).then((access) => {
        if (cancelled) return;
        setLoadedAccess((prev) => ({
          companyId,
          byProduct: {
            ...(String(prev.companyId) === String(companyId) ? prev.byProduct : {}),
            // Couldn't load: treat as no access for now (its results stay
            // hidden) instead of waiting forever. Retried on the next open.
            [productName]: access || { product_name: productName, company_id: companyId, module_details: [], failed: true },
          },
        }));
      });
    });
    return () => {
      cancelled = true;
    };
  }, [open, domain, companyId, userDetails?.id, userDetails?.type, companies, productNames, accessVersion]);
  const accessByProduct = useMemo(() => {
    const byProduct = String(loadedAccess.companyId) === String(companyId) ? { ...loadedAccess.byProduct } : {};
    // The product on screen is already loaded by the sidebar.
    const current = userScreenAccess?.product_name;
    if (current && !byProduct[current] && matchesAccessContext(userScreenAccess, companyId, current)) {
      byProduct[current] = userScreenAccess;
    }
    return byProduct;
  }, [loadedAccess, companyId, userScreenAccess]);

  const showEmployees = useMemo(() => canOpenEmployeePanel(registry), [registry]);
  // true / false / null (that product's access still loading).
  const accessOf = useCallback(
    (item) => {
      const context = { domain, companyId };
      if (item?.type === "employee") {
        return showEmployees ? canOpenRoute(routes, accessByProduct, context, EMPLOYEE_PAGE_PATH) : false;
      }
      return canOpenRoute(routes, accessByProduct, context, item?.route);
    },
    [routes, accessByProduct, domain, companyId, showEmployees],
  );
  const allowedOnly = useCallback((items) => (items || []).filter((item) => accessOf(item) === true), [accessOf]);
  const anyPending = useCallback((items) => (items || []).some((item) => accessOf(item) === null), [accessOf]);

  const quickActions = useMemo(
    () => quickActionsFor(allowedOnly(registry), currentLocation),
    [registry, currentLocation, allowedOnly],
  );

  // ---- Searching: 200 ms after the last key; a newer search cancels the
  // older request, so an old response never replaces a newer one. ----
  const trimmed = query.trim();
  const typing = isLongEnough(trimmed);
  useEffect(() => {
    setSearchFailed(false);
    // While the next search runs, the current results stay on screen (no
    // shrink to "Searching…" on every key). Cleared only when not searching.
    if (!open || !typing || !domain) {
      setResults(null);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      search({
        domain,
        companyId,
        query: trimmed,
        limit: seeAll ? SEE_ALL_LIMIT : GROUP_LIMIT,
        signal: controller.signal,
      })
        .then((found) => !controller.signal.aborted && setResults(found))
        .catch((error) => {
          if (controller.signal.aborted || error?.name === "AbortError") return;
          setSearchFailed(true);
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, typing, trimmed, domain, companyId, seeAll]);

  // The rows on screen, in order, for the keyboard.
  const beforeTypingPending = !typing && (recent === null || anyPending(recent) || anyPending(registry));
  // Pages: the server's, plus the user's own sidebar pages the server
  // doesn't list yet (localPages.js).
  const pagesWithLocal = useMemo(
    () =>
      results
        ? withLocalPages({
            serverPages: allowedOnly(results.pages),
            query: trimmed,
            routes,
            accessByProduct,
            context: { domain, companyId },
            limit: seeAll ? SEE_ALL_LIMIT : GROUP_LIMIT,
          })
        : { pages: [], more: false },
    [results, allowedOnly, trimmed, routes, accessByProduct, domain, companyId, seeAll],
  );
  const sections = useMemo(() => {
    if (!typing) {
      // Recent and quick actions appear together, once both are ready.
      if (beforeTypingPending) return [];
      return [
        { key: "recent", label: "Recent", items: allowedOnly(recent) },
        { key: "quick", label: "Quick actions", items: quickActions },
      ].filter((section) => section.items.length > 0);
    }
    if (!results) return [];
    return GROUPS.map((group) => ({
      ...group,
      items: group.key === "pages" ? pagesWithLocal.pages : allowedOnly(results[group.key]),
    })).filter((section) => section.items.length > 0);
  }, [typing, results, recent, quickActions, allowedOnly, beforeTypingPending, pagesWithLocal]);
  // Results held back only because their product's access is still loading.
  const accessPending = typing
    ? Boolean(results) && GROUPS.some((group) => anyPending(results[group.key]))
    : beforeTypingPending;
  const flatItems = useMemo(() => sections.flatMap((section) => section.items), [sections]);
  const hasMore =
    typing && !seeAll && results &&
    sections.some((section) => results.more?.[section.key] || (section.key === "pages" && pagesWithLocal.more));

  // Development only: what the API returned and what the second check hid,
  // in the browser console ("[Global search]"), to tell a backend gap
  // (nothing returned) from a frontend one (returned but hidden).
  useEffect(() => {
    if (process.env.NODE_ENV !== "development" || !typing || !results || accessPending) return;
    const hidden = GROUPS.flatMap((group) =>
      (results[group.key] || [])
        .filter((item) => accessOf(item) !== true)
        .map((item) => ({ group: group.key, title: item.title, route: item.route })),
    );
    // eslint-disable-next-line no-console
    console.info(
      `[Global search] "${trimmed}" — API returned`,
      Object.fromEntries(GROUPS.map((group) => [group.key, (results[group.key] || []).map((item) => item.title)])),
      hidden.length ? { hiddenByPermissionCheck: hidden } : "(nothing hidden)",
    );
  }, [results, accessPending, typing, trimmed, accessOf]);

  // The first row is pre-selected whenever the rows change. Keyed on the
  // rows themselves, so a background refresh that rebuilds the same list
  // (e.g. permissions reloaded on window focus) keeps the user's selection.
  const rowsKey = flatItems.map((item) => `${item.type}:${item.id}:${item.route}`).join("|");
  useEffect(() => {
    setActiveIndex(0);
  }, [rowsKey]);
  useEffect(() => {
    setSeeAll(false);
  }, [trimmed]);
  useEffect(() => {
    if (!open) return;
    document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setResults(null);
    setSeeAll(false);
  }, []);

  const openEntry = useCallback(
    (entry, newTab = false) => {
      if (!entry?.route) return;
      const target = entry.type === "employee" ? { ...entry, route: employeePanelRoute(entry) } : entry;
      if (newTab) {
        window.open(target.route, "_blank", "noopener");
        return;
      }
      close();
      inputRef.current?.blur();
      if (onOpen) onOpen(target);
      else history.push(target.route);
    },
    [close, history, onOpen],
  );

  // Ctrl/Cmd + K anywhere, or "/" when not typing in a field, opens it.
  useEffect(() => {
    const onKeyDown = (event) => {
      const isShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k";
      const isSlash = event.key === "/" && !event.ctrlKey && !event.metaKey && !event.altKey && !isTypingTarget(event.target);
      if (!isShortcut && !isSlash) return;
      event.preventDefault();
      setOpen(true);
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // A page change (from anywhere) closes it.
  useEffect(() => {
    close();
  }, [location.pathname, close]);

  const handleKeyDown = (event) => {
    const count = flatItems.length;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      if (count) setActiveIndex((index) => (index + 1) % count);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (count) setActiveIndex((index) => (index - 1 + count) % count);
    } else if (event.key === "Enter") {
      event.preventDefault();
      openEntry(flatItems[activeIndex], event.ctrlKey || event.metaKey);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
      inputRef.current?.blur();
    } else if (event.key === "Tab") {
      close();
    }
  };

  const showPanel = open;
  // The dropdown is exactly as wide as the search box (followed live as
  // the window or sidebar resizes).
  const [panelWidth, setPanelWidth] = useState(0);
  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return undefined;
    const update = () => setPanelWidth(anchor.offsetWidth);
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(anchor);
    return () => observer.disconnect();
  }, []);
  let rowIndex = -1;

  const renderRow = (entry) => {
    rowIndex += 1;
    const index = rowIndex;
    const active = index === activeIndex;
    const employee = entry.type === "employee";
    const Icon = employee ? null : TYPE_ICON[entry.type] || DescriptionOutlined;
    // Employees: the second line is "code · site", and highlight_field says
    // whether the ranges are on the name or that line. Others: the path,
    // ranges always on the title.
    const secondLine = employee ? entry.subtitle : entry.path;
    const titleRanges = !employee || entry.highlight_field === "title" ? entry.highlight : [];
    const lineRanges = employee && entry.highlight_field === "subtitle" ? entry.highlight : [];
    return (
      <Box
        key={`${entry.id}-${index}`}
        id={optionId(index)}
        role="option"
        aria-selected={active}
        onMouseMove={() => !active && setActiveIndex(index)}
        // Keep focus in the input so Esc and the arrows keep working.
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => openEntry(entry, event.ctrlKey || event.metaKey)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.25,
          py: 0.75,
          mx: 0.75,
          borderRadius: designRadii.control,
          cursor: "pointer",
          backgroundColor: active ? designColors.tealTint : "transparent",
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: entry.type === "employee" ? "50%" : "8px",
            backgroundColor: entry.type === "employee" ? designColors.slate200 : designColors.slateBg,
            border: entry.type === "employee" ? "none" : `1px solid ${designColors.slate200}`,
            color: designColors.slate600,
            fontSize: 11,
            fontWeight: designWeights.heading,
            fontFamily: designFontFamily,
          }}
        >
          {Icon ? <Icon sx={{ fontSize: 16 }} /> : initialsOf(entry.title)}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <Typography noWrap sx={{ fontFamily: designFontFamily, fontSize: 13, fontWeight: designWeights.semibold, color: designColors.slate900 }}>
              <Highlighted text={entry.title || ""} ranges={titleRanges} />
            </Typography>
            {entry.tag ? (
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  px: 0.75,
                  borderRadius: "10px",
                  fontFamily: designFontFamily,
                  fontSize: 10.5,
                  lineHeight: "18px",
                  fontWeight: designWeights.semibold,
                  color: designColors.slate600,
                  backgroundColor: designColors.slate100,
                }}
              >
                {entry.tag}
              </Box>
            ) : null}
          </Box>
          {secondLine ? (
            <Typography noWrap sx={{ fontFamily: designFontFamily, fontSize: 11.5, color: designColors.slate600 }}>
              <Highlighted text={secondLine} ranges={lineRanges} />
            </Typography>
          ) : null}
        </Box>
        {active ? <Kbd>Enter</Kbd> : null}
      </Box>
    );
  };

  const renderBody = () => {
    if (typing && sections.length === 0) {
      if (searchFailed) {
        return (
          <Typography sx={{ px: 2, py: 2.5, fontFamily: designFontFamily, fontSize: 13, color: designColors.slate600, textAlign: "center" }}>
            Couldn't search right now. Please try again.
          </Typography>
        );
      }
      if (!results || accessPending) {
        return (
          <Typography sx={{ px: 2, py: 2.5, fontFamily: designFontFamily, fontSize: 13, color: designColors.slate600, textAlign: "center" }}>
            Searching…
          </Typography>
        );
      }
      return (
        <Box sx={{ px: 3, py: 3, textAlign: "center" }}>
          <SearchIcon sx={{ fontSize: 22, color: designColors.slate400 }} />
          <Typography sx={{ mt: 0.5, fontFamily: designFontFamily, fontSize: 13.5, fontWeight: designWeights.heading, color: designColors.slate900 }}>
            No results for “{trimmed}”
          </Typography>
          <Typography sx={{ mt: 0.5, fontFamily: designFontFamily, fontSize: 12, color: designColors.slate600 }}>
            Check the spelling, or try a shorter word. You only see pages and employees your role can open.
          </Typography>
        </Box>
      );
    }
    if (!typing && sections.length === 0) {
      return (
        <Typography sx={{ px: 2, py: 2.5, fontFamily: designFontFamily, fontSize: 13, color: designColors.slate600, textAlign: "center" }}>
          {accessPending ? "Loading…" : "Type at least 2 letters to search pages, settings and employees."}
        </Typography>
      );
    }
    return (
      <>
        {sections.map((section) => (
          <Box key={section.key} role="group" aria-label={section.label} sx={{ pb: 0.5 }}>
            <Typography
              sx={{
                px: 2,
                pt: 1.25,
                pb: 0.5,
                fontFamily: designFontFamily,
                fontSize: 10.5,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontWeight: designWeights.heading,
                color: designColors.slate600,
              }}
            >
              {section.label}
            </Typography>
            {section.items.map(renderRow)}
          </Box>
        ))}
        {hasMore ? (
          <Box
            component="button"
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setSeeAll(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mx: 2,
              my: 0.75,
              p: 0,
              border: 0,
              background: "none",
              cursor: "pointer",
              fontFamily: designFontFamily,
              fontSize: 12.5,
              fontWeight: designWeights.heading,
              color: designColors.tealDark,
            }}
          >
            See all results for “{trimmed}” <ArrowForwardRounded sx={{ fontSize: 15 }} />
          </Box>
        ) : null}
      </>
    );
  };

  return (
    <ClickAwayListener onClickAway={() => open && close()}>
      {/* 480px wide at most: the dropdown matches it, and its footer
          hints fit on one line at that width. */}
      <Box sx={{ flex: "1 1 auto", minWidth: 140, maxWidth: 480 }}>
        <Box
          ref={anchorRef}
          onClick={() => {
            setOpen(true);
            inputRef.current?.focus();
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: 36,
            px: 1.25,
            borderRadius: designRadii.control,
            border: `1px solid ${open ? designColors.teal : designColors.borderInput}`,
            boxShadow: open ? `0 0 0 3px ${designColors.tealTint}` : "none",
            backgroundColor: designColors.white,
            cursor: "text",
            transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            "&:hover": { borderColor: open ? designColors.teal : designColors.borderInputHover },
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: designColors.slate400, flexShrink: 0 }} />
          <InputBase
            inputRef={inputRef}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Search Cauver — pages, settings, employees"
            inputProps={{
              role: "combobox",
              "aria-label": "Search Cauver",
              "aria-expanded": showPanel,
              "aria-controls": LISTBOX_ID,
              "aria-autocomplete": "list",
              "aria-activedescendant": showPanel && flatItems.length ? optionId(activeIndex) : undefined,
              autoComplete: "off",
              spellCheck: false,
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: designFontFamily,
              fontSize: 13,
              color: designColors.slate900,
              "& input::placeholder": { color: designColors.slate400, opacity: 1 },
            }}
          />
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.5, flexShrink: 0 }}>
            <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd>
            <Kbd>K</Kbd>
          </Box>
        </Box>

        {/* The page behind dims slightly; the sidebar and top bar don't. */}
        {showPanel ? (
          <Box
            aria-hidden
            onClick={close}
            sx={{
              position: "fixed",
              top: APP_BAR_HEIGHT,
              right: 0,
              bottom: 0,
              left: sidebarOpen ? `calc(${SIDEBAR_RAIL_WIDTH}px + ${SIDEBAR_PANEL_WIDTH_VAR})` : `${SIDEBAR_RAIL_WIDTH}px`,
              backgroundColor: PANEL_BACKDROP_COLOR,
              zIndex: (theme) => theme.zIndex.appBar - 1,
            }}
          />
        ) : null}

        <Popper
          open={showPanel}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          // The top bar is fixed, so the dropdown is too: scrolling the page
          // keeps it right under the box instead of carrying it away.
          popperOptions={POPPER_OPTIONS}
          modifiers={POPPER_MODIFIERS}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Box
            sx={{
              width: panelWidth || undefined,
              maxWidth: "calc(100vw - 24px)",
              backgroundColor: designColors.white,
              border: `1px solid ${designColors.slate200}`,
              borderRadius: "12px",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.16)",
              overflow: "hidden",
              fontFamily: designFontFamily,
            }}
          >
            <Box
              id={LISTBOX_ID}
              role="listbox"
              aria-label="Search results"
              sx={{ maxHeight: "min(60vh, 520px)", overflowY: "auto", py: 0.5 }}
            >
              {renderBody()}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.75,
                borderTop: `1px solid ${designColors.slate200}`,
                backgroundColor: designColors.slateBg,
                fontSize: 11,
                color: designColors.slate600,
                fontFamily: designFontFamily,
                // One line at full width: measured in the app at about 452px
                // of the 462px inside the 480px panel.
                // On a narrower screen the note moves to its own line instead
                // of being cut; words are never split.
                whiteSpace: "nowrap",
                flexWrap: "wrap",
                rowGap: 0.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd> move
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                <Kbd>Enter</Kbd> open
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                <Kbd>Esc</Kbd> close
              </Box>
              <Box sx={{ ml: "auto", flexShrink: 0 }}>Only shows what you are allowed to open</Box>
            </Box>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
